import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 2 }

import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref} from "vue";
import {ActivityRegistration, Guid} from "@/types";
import SearchInput from "@/components/layout/SearchInput.vue";
import DataTable from "@/components/layout/DataTable.vue";
import {SucceededOrNotResponse} from "@/types/responses";
import {notifyError, notifySuccess} from "@/notify";
import {useMemberService} from "@/inversify.config";
import i18n from "@/i18n";
import '@/extensions/date.extensions';

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'MemberActivityTable',
  emits: ["activityRegistrationSelected"],
  setup(__props, { emit: __emit }) {

const emit = __emit

const {t} = useI18n()
const memberService = useMemberService()

const registrationsSearchValue = ref("");
const activityRegistrations = ref<ActivityRegistration[]>([])

onMounted(async () => {
  await fetchActivityRegistration();
})

const registrationsItems = computed(() => {
  let items: any[] = [];
  if (activityRegistrations.value === null || activityRegistrations.value.length == 0)
    return []

  let lang = i18n.getLocale()

  let now = new Date();
  activityRegistrations.value.forEach((registration: ActivityRegistration) => {
    let activityName = (lang == 'fr' ? registration.activityNameFr : registration.activityNameEn) ?? '';

    let occurrenceStartDateTime = new Date(registration.activityOccurrenceStartDateAndTime ?? '');
    let status = registration.deletedActivityOccurrence
      ? t("activityOccurrences.canceled")
      : occurrenceStartDateTime > now
        ? t("activityOccurrences.upcoming")
        : t("activityOccurrences.completed");

    let actions = {
      edit: !registration.deletedActivityOccurrence,
      delete: registration.deletedActivityOccurrence
        ? false
        : new Date(registration.activityOccurrenceStartDateAndTime ?? "").hoursBetweenToday() >= 48
    }
    let hasNoActions = Object.values(actions).every(v => !v);
    items.push({
      id: registration.id,
      activityName: activityName,
      activityOccurrenceStartDate: occurrenceStartDateTime.formatToDateString(),
      activityOccurrenceStartTime: occurrenceStartDateTime.formatToTimeString(),
      firstName: registration.billingAddress?.firstName,
      lastName: registration.billingAddress?.lastName,
      email: registration.billingAddress?.email,
      participantsCount: registration.participants?.length,
      isPaid: registration.isFree ? t("global.free") : registration.isPaid ? t("global.yes") : t("global.no"),
      status: status,
      actions: hasNoActions ? undefined : actions
    })
  })

  return items
    .sort((a, b) => new Date(b.activityOccurrenceStartDate ?? "").valueOf() - new Date(a.activityOccurrenceStartDate ?? "").valueOf());
})

let actionsHeader = {
  text: t("global.table.actions"),
  value: "actions",
};

const registrationBaseHeaders = [
  {
    text: t("activities.activity"),
    value: "activityName",
    sortable: true,
  },
  {
    text: t("organization.status"),
    value: "status",
    sortable: true
  },
  {
    text: t("activities.form.startDate"),
    value: "activityOccurrenceStartDate",
    sortable: true,
  },
  {
    text: t("activities.form.startsAt"),
    value: "activityOccurrenceStartTime",
    sortable: true,
  },
  {
    text: t("participants.lastName"),
    value: "lastName",
    sortable: true,
  },
  {
    text: t("participants.firstName"),
    value: "firstName",
    sortable: true,
  },
  {
    text: t("participants.participants"),
    value: "participantsCount",
    sortable: true,
  },
  {
    text: t("invoice.isPaid"),
    value: "isPaid",
    sortable: true,
  }
];
const registrationHeaders = computed(() => {
  if (registrationsItems.value.every(item => item.actions === undefined))
    return registrationBaseHeaders;
  return [...registrationBaseHeaders, actionsHeader];
});

async function handleDelete(activityRegistration: any) {
  if (activityRegistration?.id == null || !Guid.isValid(activityRegistration.id ?? ""))
    return;

  let confirmDelete = confirm(t("registrations.form.deleteConfirmation"));
  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await memberService.deleteCurrentMemberActivityRegistration(activityRegistration.id);

  if (result?.succeeded) {
    notifySuccess(t("registrations.form.deleteSuccess"));
    await fetchActivityRegistration();
  } else {
    notifyError(t("registrations.form.deleteError"));
  }
}

function handleEdit(id: string) {
  emit("activityRegistrationSelected", id)
}

async function fetchActivityRegistration() {
  let response = await memberService.getCurrentMemberActivityRegistrations()
  activityRegistrations.value = response as ActivityRegistration[];
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (activityRegistrations.value.length)
      ? (_openBlock(), _createBlock(SearchInput, {
          key: 0,
          modelValue: registrationsSearchValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((registrationsSearchValue).value = $event)),
          class: "registrationsHeaders"
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (activityRegistrations.value.length)
      ? (_openBlock(), _createBlock(DataTable, {
          key: 1,
          headers: registrationHeaders.value,
          items: registrationsItems.value,
          "search-value": registrationsSearchValue.value,
          onDelete: handleDelete,
          onEdit: handleEdit
        }, null, 8, ["headers", "items", "search-value"]))
      : (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_unref(t)('registrations.noRegistrations')), 1))
  ], 64))
}
}

})